class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
    this.setupQuickAddModalRecovery();
  }

  setupQuickAddModalRecovery() {
    document.body.addEventListener('modalClosed', () => {
      const mobileSearchOpen = document.querySelector('.search-modal-active');
      if (!this.isOpen && !mobileSearchOpen) return;
      document.documentElement.classList.add('overflow-hidden');
      document.body.classList.add('overflow-hidden');
      if (mobileSearchOpen) {
        document.body.style.overflow = 'hidden';
      }
    });
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // GTM: add_to_cart_from_search — event delegation on results container
    this.predictiveSearchResults.addEventListener('click', (event) => {
      const addToCartBtn = event.target.closest('.quick-add__submit');
      if (!addToCartBtn) return;

      const productCard = addToCartBtn.closest('.card-wrapper');
      const productTitle = productCard?.querySelector('.card__heading a')?.textContent?.trim() || '';
      // Product ID is embedded in modal-opener's data-modal: "#QuickAddInfo-{productId}-{sectionId}"
      const modalOpener = addToCartBtn.closest('modal-opener');
      const modalId = modalOpener?.getAttribute('data-modal') || '';
      const productId = modalId.match(/(\d+)\s*$/)?.[1] || '';
      // Product URL from modal-opener button
      const productUrl = modalOpener?.querySelector('button')?.getAttribute('data-product-url') || '';

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'add_to_cart_from_search',
        search_term: this.searchTerm,
        product_title: productTitle,
        product_id: productId,
        product_url: productUrl,
      });
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.restoreInitialContent();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      const activeEl = document.activeElement;
      // Don't close if a quick-add modal is currently open
      if (document.querySelector('quick-add-modal[open]')) return;
      // Don't close if cart drawer is open
      if (document.querySelector('cart-drawer.active')) return;
      // Don't close if focus moved to a quick-add button (modal fetch in progress, not yet open)
      if (activeEl && activeEl.closest('modal-opener[data-modal]')) return;
      // Mobile fullscreen search: keep results open while modal is active
      if (window.matchMedia('(max-width: 749px)').matches && document.querySelector('.search-modal-active')) {
        return;
      }
      if (!this.contains(activeEl)) this.close();
    }, 300);
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.restoreInitialContent();
      return;
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    if (!window.ThriveCustomSearch || typeof window.ThriveCustomSearch.fetchAndBuildMarkup !== 'function') {
      this.close();
      return;
    }

    window.ThriveCustomSearch.fetchAndBuildMarkup(searchTerm, this.abortController.signal)
      .then((resultsMarkup) => {
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup, searchTerm);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (!this.statusElement || statusText == null) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  restoreInitialContent() {
    this.abortController.abort();
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.removeAttribute('results');
    this.removeAttribute('loading');

    const container = this.predictiveSearchResults;
    if (!container) return;

    const resultsRoot = container.querySelector('#predictive-search-results');
    if (resultsRoot) resultsRoot.remove();

    container.classList.add('sis-empty');
    container.classList.remove('sis-has-query');

    if (!container.querySelector('.sis-initial-content')) {
      this.dispatchEvent(
        new CustomEvent('restore-initial-content', {
          bubbles: true,
          detail: { container: container },
        })
      );
    }

    if (!container.querySelector('.predictive-search__loading-state')) {
      container.insertAdjacentHTML(
        'beforeend',
        '<div class="predictive-search__loading-state" style="display:none;"></div>'
      );
    }

    if (document.activeElement === this.input) {
      container.setAttribute('open', '');
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
      this.predictiveSearchResults.style.maxHeight =
        this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      document.documentElement.classList.add('overflow-hidden');
      document.body.classList.add('overflow-hidden');
    } else {
      this.closeResults(false);
    }
  }

  renderSearchResults(resultsMarkup, searchTerm) {
    // Check if no results BEFORE rendering to avoid flicker
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = resultsMarkup;
    const hasResults = tempDiv.querySelector('#predictive-search-results-groups-wrapper');

    if (!hasResults) {
      const container = this.predictiveSearchResults;
      if (container) {
        const resultsRoot = container.querySelector('#predictive-search-results');
        if (resultsRoot) resultsRoot.remove();
        this.removeAttribute('results');
        container.classList.add('sis-empty');
        container.classList.remove('sis-has-query');
        if (!container.querySelector('.sis-initial-content')) {
          this.dispatchEvent(
            new CustomEvent('restore-initial-content', {
              bubbles: true,
              detail: { container: container },
            })
          );
        }
        if (!container.querySelector('.predictive-search__loading-state')) {
          container.insertAdjacentHTML(
            'beforeend',
            '<div class="predictive-search__loading-state" style="display:none;"></div>'
          );
        }
      }
      this.open();
      return;
    }

    // Normal rendering for actual results
    this.cleanupSearchQuickAddModals();
    const container = this.predictiveSearchResults;
    if (container) {
      container.classList.remove('sis-empty');
      container.classList.add('sis-has-query');
    }
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();

    document.dispatchEvent(new CustomEvent('theme:section:load', { bubbles: true }));

    // GTM: search event — use passed searchTerm to avoid race conditions
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'search',
      search_term: searchTerm || this.searchTerm,
    });
  }

  cleanupSearchQuickAddModals() {
    document.querySelectorAll('quick-add-modal[open]').forEach((modal) => {
      if (typeof modal.hide === 'function') {
        modal.hide(true);
      }
    });

    const activeModalIds = new Set();
    this.predictiveSearchResults.querySelectorAll('modal-opener[data-modal]').forEach((opener) => {
      const id = opener.getAttribute('data-modal')?.replace('#', '');
      if (id) activeModalIds.add(id);
    });

    document
      .querySelectorAll(
        'quick-add-modal[id^="QuickAdd-predictive-search-"], quick-add-modal[id^="QuickAdd-header-bestsellers-"]'
      )
      .forEach((modal) => {
        if (!activeModalIds.has(modal.id)) {
          modal.remove();
        }
      });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegion =
      this.querySelector('[data-predictive-search-live-region-count-value]') ||
      this.predictiveSearchResults?.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion) {
      this.setLiveRegionText(liveRegion.textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    // Keep in sync with closeResults() — theme CSS keys off .predictive-search--header[open]
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.setAttribute('open', '');
    }
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    // Prevent body scroll when search dropdown is open
    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    // Theme CSS uses .predictive-search--header[open] — clear that too
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('open');
    }
    // Remove body scroll lock when search dropdown closes
    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('predictive-search', PredictiveSearch);
